<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { Input } from "@/shared/ui/input";
import Button from "@/shared/ui/button/Button.vue";
import { UserAvatar } from "@/shared/ui/user-avatar";
import { getUserList } from "@/entities/user";

defineProps({
  selectedUser: { type: Object, default: null },
});
const emit = defineEmits(["select", "clear"]);

const search = ref("");
const results = ref([]);
const loading = ref(false);
const showDropdown = ref(false);
const containerRef = ref(null);

let debounceTimer = null;

async function runSearch(q) {
  loading.value = true;
  try {
    const data = await getUserList({
      q: q?.trim() || undefined,
      page_size: 20,
      page: 1,
    });
    results.value = data?.success ? data.payload?.items || [] : [];
  } finally {
    loading.value = false;
  }
}

watch(search, (q) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => runSearch(q), 250);
});

function onFocus() {
  showDropdown.value = true;
  if (results.value.length === 0 && !loading.value) {
    runSearch(search.value);
  }
}

function onSelect(user) {
  emit("select", user);
  showDropdown.value = false;
  search.value = "";
  results.value = [];
}

function clearSelection() {
  emit("clear");
  search.value = "";
  results.value = [];
  showDropdown.value = false;
}

function userLabel(u) {
  const name = [u.last_name, u.first_name, u.middle_name]
    .filter(Boolean)
    .join(" ");
  return name || u.identifier || u.uuid;
}

function onDocumentClick(e) {
  if (containerRef.value && !containerRef.value.contains(e.target)) {
    showDropdown.value = false;
  }
}

onMounted(() => {
  document.addEventListener("mousedown", onDocumentClick);
  runSearch("");
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocumentClick);
});
</script>

<template>
  <div
    ref="containerRef"
    class="rounded-lg border border-border bg-card p-4 flex flex-col gap-3"
  >
    <div class="flex items-baseline justify-between gap-3">
      <div class="text-sm font-medium text-foreground">
        {{ $t("Пользователь") }}
        <span class="text-destructive">*</span>
      </div>
      <div v-if="!selectedUser" class="text-xs text-muted-foreground">
        {{ $t("Поиск по ФИО или ИИН") }}
      </div>
    </div>

    <div v-if="selectedUser" class="flex items-center justify-between gap-3">
      <div class="flex items-center gap-3 min-w-0">
        <UserAvatar
          :identifier="selectedUser.identifier"
          :seed="selectedUser.uuid"
          :name="`${selectedUser.first_name} ${selectedUser.last_name}`"
          :size="36"
        />
        <div class="flex flex-col min-w-0">
          <div class="text-sm font-medium text-foreground truncate">
            {{ userLabel(selectedUser) }}
          </div>
          <div class="text-xs text-muted-foreground truncate">
            {{ selectedUser.identifier }}
          </div>
        </div>
      </div>
      <Button variant="outline" size="sm" @click="clearSelection">
        {{ $t("Сменить") }}
      </Button>
    </div>

    <div v-else class="relative">
      <Input
        v-model="search"
        :placeholder="$t('Начните вводить ФИО или ИИН')"
        clearable
        @focus="onFocus"
      />

      <div
        v-if="showDropdown"
        class="absolute left-0 right-0 top-full mt-1 z-50 max-h-72 overflow-y-auto rounded-md border border-border bg-popover shadow-md"
      >
        <div v-if="loading" class="px-3 py-2 text-sm text-muted-foreground">
          {{ $t("Загрузка...") }}
        </div>
        <div
          v-else-if="!results.length"
          class="px-3 py-2 text-sm text-muted-foreground"
        >
          {{ $t("Ничего не найдено") }}
        </div>
        <button
          v-for="u in results"
          :key="u.uuid"
          type="button"
          class="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent transition-colors"
          @click="onSelect(u)"
        >
          <UserAvatar
            :identifier="u.identifier"
            :seed="u.uuid"
            :name="`${u.first_name} ${u.last_name}`"
            :size="28"
          />
          <div class="flex flex-col min-w-0">
            <div class="text-sm text-foreground truncate">
              {{ userLabel(u) }}
            </div>
            <div class="text-xs text-muted-foreground truncate">
              {{ u.identifier }}{{ u.email ? ` · ${u.email}` : "" }}
            </div>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>
